import { Send, Check, X, Download, Plus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import AIAssistantPanel from "./AIAssistantPanel";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; }
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between text-[12px] py-0.5"><span className="text-slate-500">{k}</span><span className="text-slate-900 font-medium">{v ?? "—"}</span></div>;
}

function FlowStep({ label, active, done }) {
  return (
    <span className={`px-2 py-0.5 rounded ${active ? "bg-primary text-white" : done ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400"}`}>
      {label}
    </span>
  );
}

/**
 * Panel lateral del editor de documentos: asistente IA (cuando está abierto) o
 * los bloques de información, flujo de estado, vigencia, controles y aprobación.
 * Extraído de DocumentEditor para reducir su complejidad.
 */
export default function DocumentSidebar({
  doc, user, related, editable, docId, selectedText,
  permissions, aiOpen, onCloseAi, onInsert, onReplace,
  onOpenSubmit, onApprove, onOpenReject, onDownloadPdf, onObsolete,
  onOpenAddControl, onRemoveControl,
}) {
  const { canSubmit, canApproveReject, canExportPdf, canObsolete } = permissions;

  if (aiOpen) {
    return (
      <div className="space-y-4">
        <AIAssistantPanel
          open={aiOpen}
          onClose={onCloseAi}
          docId={docId}
          selectedText={selectedText}
          onInsert={onInsert}
          onReplace={onReplace}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Section title="Información">
        <Row k="Tipo" v={doc.type} />
        <Row k="Versión" v={doc.current_version} />
        <Row k="Generado por IA" v={doc.generated_by_ai ? "Sí" : "No"} />
        <Row k="Creado" v={fmt(doc.created_at)} />
        <Row k="Modificado" v={fmt(doc.updated_at)} />
      </Section>

      <Section title="Estado del flujo">
        <div className="flex items-center gap-1 text-[12px] mb-3" data-testid="flow-diagram">
          <FlowStep label="Borrador" active={doc.status === "draft"} done={["review", "approved"].includes(doc.status)} />
          <span className="text-slate-300">→</span>
          <FlowStep label="Revisión" active={doc.status === "review"} done={doc.status === "approved"} />
          <span className="text-slate-300">→</span>
          <FlowStep label="Aprobado" active={doc.status === "approved"} done={false} />
        </div>
        <div className="space-y-2">
          {canSubmit && <Button size="sm" className="w-full" onClick={onOpenSubmit} data-testid="btn-submit-review"><Send className="h-3 w-3 mr-1" /> Enviar a revisión</Button>}
          {canApproveReject && (
            <>
              <Button size="sm" className="w-full" onClick={onApprove} data-testid="btn-approve"><Check className="h-3 w-3 mr-1" /> Aprobar</Button>
              <Button size="sm" variant="outline" className="w-full" onClick={onOpenReject} data-testid="btn-reject"><X className="h-3 w-3 mr-1" /> Rechazar</Button>
            </>
          )}
          {canExportPdf && <Button size="sm" variant="outline" className="w-full" onClick={onDownloadPdf} data-testid="btn-pdf"><Download className="h-3 w-3 mr-1" /> Exportar PDF</Button>}
          {canObsolete && <Button size="sm" variant="ghost" className="w-full text-destructive" onClick={onObsolete} data-testid="btn-obsolete">Marcar obsoleto</Button>}
        </div>
      </Section>

      {doc.status === "approved" && (
        <Section title="Vigencia">
          <Row k="Desde" v={fmt(doc.valid_from)} />
          <Row k="Hasta" v={fmt(doc.valid_until)} />
          <Row k="Próxima revisión" v={fmt(doc.next_review_date)} />
        </Section>
      )}

      <Section title={`Controles relacionados (${related.length})`}>
        <div className="flex flex-wrap gap-1">
          {related.map((c) => (
            <span key={c} className="text-[12px] border border-slate-200 rounded px-2 py-0.5 text-slate-700 flex items-center gap-1" data-testid={`ctrl-chip-${c}`}>
              {c}
              {editable && <button onClick={() => onRemoveControl(c)} className="text-slate-400 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>}
            </span>
          ))}
          {editable && (
            <button onClick={onOpenAddControl} className="text-[12px] text-primary border border-primary/30 rounded px-2 py-0.5 hover:bg-primary/5" data-testid="btn-add-control">
              <Plus className="h-3 w-3 inline" /> añadir
            </button>
          )}
        </div>
      </Section>

      {(doc.status === "approved" || doc.status === "obsolete") && doc.approved_by_user_id && (
        <Section title="Aprobación">
          <div className="text-[12px] text-slate-600">Aprobado por <b>{doc.approved_by_user_id === user.id ? user.name : "usuario " + (doc.approved_by_user_id || "").slice(0, 8)}</b> el {fmt(doc.valid_from)}.</div>
        </Section>
      )}
    </div>
  );
}
